import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Button,
  VStack,
  HStack,
  Flex,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Spinner,
  Select,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  FaUserPlus,
  FaBoxOpen,
  FaPhoneAlt,
  FaRoute,
  FaTruck,
  FaArrowRight,
  FaSync,
  FaCheckCircle,
  FaRegCircle,
} from 'react-icons/fa';
import { isToday, isTomorrow, startOfDay, endOfDay, addDays } from 'date-fns';
import { supabase } from '../../supabaseClient';
import { getPhlebReadinessStage } from '../../utils/phlebReadiness';
import { getNextUpdateInfo, fmtDur } from '../../utils/submissionSla';

/* ───────────────────────────────────────────────────────────────────────────
   Daily Tasks — the "what do I do today" board that greets admins on login.
   Four pillars, each an actionable queue that empties as you work it:
     1. Onboard phlebs        (readiness pipeline)
     2. Call patients         (next-update SLA queue)
     3. Assign routes         (unassigned upcoming draws)
     4. Specimen handoff      (drop-off / pickup coordination)
   All data comes from existing tables — this is a triage view, not new plumbing.
─────────────────────────────────────────────────────────────────────────── */

const daysSince = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const ageLabel = (ts) => {
  const n = daysSince(ts);
  if (n == null) return '';
  if (n === 0) return 'today';
  if (n === 1) return '1 day';
  return `${n} days`;
};

/* ─── Presentational helpers (module scope so they don't remount on each
       live-countdown tick, which would reset list scroll positions) ───────── */
function SectionCard({ icon, color, title, subtitle, count, children, footer, colors }) {
  return (
    <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.borderColor} shadow="sm" h="100%">
      <CardHeader pb={2}>
        <HStack justify="space-between" align="flex-start">
          <HStack spacing={3} align="flex-start">
            <Box bg={`${color}.50`} color={`${color}.600`} p={2} borderRadius="lg">
              <Icon as={icon} boxSize={4} />
            </Box>
            <Box>
              <Heading size="sm" fontWeight="700">{title}</Heading>
              <Text fontSize="xs" color={colors.mutedColor}>{subtitle}</Text>
            </Box>
          </HStack>
          <Badge colorScheme={count > 0 ? color : 'gray'} borderRadius="full" px={2.5} py={1} fontSize="sm">
            {count}
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <Box maxH="340px" overflowY="auto" overflowX="hidden">
          {children}
        </Box>
        {footer}
      </CardBody>
    </Card>
  );
}

function EmptyRow({ label, colors }) {
  return (
    <VStack py={6} spacing={2} color={colors.mutedColor}>
      <Icon as={FaCheckCircle} boxSize={6} color="green.400" />
      <Text fontSize="sm">{label}</Text>
    </VStack>
  );
}

function Row({ children, onClick, colors }) {
  return (
    <Flex
      py={2.5}
      gap={3}
      align="center"
      borderBottom="1px solid"
      borderColor={colors.rowBorder}
      _hover={onClick ? { bg: colors.hoverBg } : undefined}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      borderRadius="md"
      px={1}
    >
      {children}
    </Flex>
  );
}

function DailyTasks() {
  const { adminUser } = useOutletContext();
  const navigate = useNavigate();
  const toast = useToast();

  const colors = {
    cardBg: useColorModeValue('white', 'gray.800'),
    borderColor: useColorModeValue('gray.200', 'gray.600'),
    mutedColor: useColorModeValue('gray.600', 'gray.400'),
    hoverBg: useColorModeValue('gray.50', 'gray.700'),
    rowBorder: useColorModeValue('gray.100', 'gray.700'),
  };

  const [loading, setLoading] = useState(true);
  const [phlebs, setPhlebs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [dropOffLocations, setDropOffLocations] = useState([]);
  const [routeReadyCount, setRouteReadyCount] = useState(0);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [, setNowTick] = useState(0);

  /* ─── Fetch everything the board needs ──────────────────────────────────── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [phlebRes, subRes, locRes, readyRes] = await Promise.all([
        // Phlebs not yet cleared for routes — the onboarding pipeline.
        supabase
          .from('phlebotomist_profiles')
          .select('id, full_name, company_name, email, onboarding_status, onboarding_started_at, onboarding_completed_at, supplies_picked_up_at, route_ready_at, agreement_signed, w9_signed_at')
          .is('route_ready_at', null)
          .order('onboarding_started_at', { ascending: true, nullsFirst: false }),

        // Open submissions: actionable for calling, due-soon for routing, or
        // drawn-but-not-handed-off for specimen logistics.
        supabase
          .from('submissions')
          .select('id, draw_code, patient_name, patient_phone, status, outcome_code, outcome_logged_at, blood_collection_time, phlebotomist_id, phlebotomist_name, handoff_method, drop_off_location_id, pickup_location, dropped_off_at, picked_up_at, followup_required, stat_test')
          .not('status', 'in', '(completed,cancelled,out_of_area)')
          .limit(1000),

        supabase
          .from('drop_off_locations')
          .select('id, name, address, active')
          .eq('active', true)
          .order('name', { ascending: true }),

        supabase
          .from('phlebotomist_profiles')
          .select('id', { count: 'exact', head: true })
          .not('route_ready_at', 'is', null),
      ]);

      if (phlebRes.error) throw phlebRes.error;
      if (subRes.error) throw subRes.error;

      setPhlebs(phlebRes.data || []);
      setSubmissions(subRes.data || []);
      setDropOffLocations(locRes.data || []);
      setRouteReadyCount(readyRes.count || 0);
    } catch (err) {
      console.error('Daily tasks fetch error:', err);
      setError(err.message || String(err));
      toast({ title: 'Failed to load tasks', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Keep SLA countdowns / overdue badges live without refetching.
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  /* ─── Onboarding pipeline ───────────────────────────────────────────────── */
  const onboarding = useMemo(() => {
    return (phlebs || [])
      .map((p) => {
        const stage = getPhlebReadinessStage(p);
        const anchor =
          stage.code === 'supplies_picked_up' ? p.supplies_picked_up_at
          : stage.code === 'awaiting_supplies' ? p.onboarding_completed_at
          : p.onboarding_started_at;
        return { ...p, stage, anchor };
      })
      .sort((a, b) => (new Date(a.anchor || 0)) - (new Date(b.anchor || 0)));
  }, [phlebs]);

  /* ─── Call queue: actionable draws past their next-update SLA (or never
         touched, or explicitly flagged for follow-up) ────────────────────── */
  const callQueue = useMemo(() => {
    return (submissions || [])
      .map((s) => ({ s, info: getNextUpdateInfo(s) }))
      .filter(({ s, info }) => info.state === 'overdue' || info.state === 'none' || s.followup_required)
      .sort((a, b) => {
        // Most overdue first, then never-contacted, then the rest.
        const rank = (x) => (x.info.state === 'overdue' ? 0 : x.info.state === 'none' ? 1 : 2);
        if (rank(a) !== rank(b)) return rank(a) - rank(b);
        return (a.info.dueMs ?? 0) - (b.info.dueMs ?? 0);
      })
      .map(({ s }) => s);
  }, [submissions]);

  /* ─── Route assignment: unassigned draws due today/tomorrow ─────────────── */
  const routeQueue = useMemo(() => {
    const horizon = endOfDay(addDays(new Date(), 1));
    return (submissions || [])
      .filter((s) => !s.phlebotomist_id && s.blood_collection_time)
      .filter((s) => {
        const d = new Date(s.blood_collection_time);
        return !isNaN(d) && d <= horizon;
      })
      .sort((a, b) => new Date(a.blood_collection_time) - new Date(b.blood_collection_time));
  }, [submissions]);

  /* ─── Specimen handoff: drawn but not yet dropped off / picked up ───────── */
  const handoffQueue = useMemo(() => {
    return (submissions || [])
      .filter((s) => {
        if (s.dropped_off_at || s.picked_up_at) return false; // already handed off
        return (s.outcome_code || '') === 'successful_draw' || s.status === 'waiting_to_be_received';
      })
      .sort((a, b) => new Date(a.outcome_logged_at || 0) - new Date(b.outcome_logged_at || 0));
  }, [submissions]);

  /* ─── Inline mutations ──────────────────────────────────────────────────── */
  const markSuppliesPickedUp = async (phleb) => {
    setSavingId(`supplies-${phleb.id}`);
    try {
      const patch = { supplies_picked_up_at: new Date().toISOString(), supplies_picked_up_by: adminUser?.name || 'admin' };
      const { error } = await supabase.from('phlebotomist_profiles').update(patch).eq('id', phleb.id);
      if (error) throw error;
      setPhlebs((prev) => prev.map((p) => (p.id === phleb.id ? { ...p, ...patch } : p)));
      toast({ title: 'Supplies marked picked up', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  };

  const assignDropOffLocation = async (sub, locationId) => {
    setSavingId(`loc-${sub.id}`);
    try {
      const patch = { drop_off_location_id: locationId || null, handoff_method: 'dropoff' };
      const { error } = await supabase.from('submissions').update(patch).eq('id', sub.id);
      if (error) throw error;
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, ...patch } : s)));
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  };

  const markHandedOff = async (sub) => {
    setSavingId(`handoff-${sub.id}`);
    try {
      const field = sub.handoff_method === 'pickup' ? 'picked_up_at' : 'dropped_off_at';
      const patch = { [field]: new Date().toISOString(), status: 'completed' };
      const { error } = await supabase.from('submissions').update(patch).eq('id', sub.id);
      if (error) throw error;
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, ...patch } : s)));
      toast({ title: sub.handoff_method === 'pickup' ? 'Marked picked up' : 'Marked dropped off', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  };

  /* ─── Count strip ───────────────────────────────────────────────────────── */
  const counts = {
    onboarding: onboarding.length,
    awaitingSupplies: onboarding.filter((p) => p.stage.code === 'awaiting_supplies').length,
    calls: callQueue.length,
    routes: routeQueue.length,
    handoff: handoffQueue.length,
  };
  const allClear = !loading && counts.onboarding === 0 && counts.calls === 0 && counts.routes === 0 && counts.handoff === 0;

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="flex-start" mb={5} flexWrap="wrap" gap={3}>
        <Box>
          <Heading as="h1" size="lg" fontWeight="700" mb={1}>
            Today's Tasks{adminUser?.name ? `, ${adminUser.name}` : ''}
          </Heading>
          <Text color={colors.mutedColor} fontSize="sm">
            Onboard phlebs · call patients · assign routes · coordinate specimen handoff — clear it by end of day.
          </Text>
        </Box>
        <IconButton aria-label="Refresh" icon={<Icon as={FaSync} />} size="sm" variant="outline" onClick={fetchAll} isLoading={loading} />
      </Flex>

      {/* Count strip */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={6}>
        {[
          { label: 'Phlebs onboarding', value: counts.onboarding, sub: counts.awaitingSupplies ? `${counts.awaitingSupplies} awaiting supplies` : null, color: 'blue', icon: FaUserPlus },
          { label: 'Patients to call', value: counts.calls, sub: null, color: 'red', icon: FaPhoneAlt },
          { label: 'Routes to assign', value: counts.routes, sub: `${routeReadyCount} phlebs route-ready`, color: 'purple', icon: FaRoute },
          { label: 'Specimens to hand off', value: counts.handoff, sub: null, color: 'teal', icon: FaTruck },
        ].map((c) => (
          <Card key={c.label} bg={colors.cardBg} borderWidth="1px" borderColor={colors.borderColor} shadow="sm">
            <CardBody py={3} px={4}>
              <Flex justify="space-between" align="flex-start">
                <Box>
                  <Text fontSize="xs" color={colors.mutedColor} fontWeight="500" mb={1}>{c.label}</Text>
                  <Heading size="lg" fontWeight="800" color={c.value > 0 ? `${c.color}.600` : undefined}>
                    {loading ? <Spinner size="sm" /> : c.value}
                  </Heading>
                  {c.sub && !loading && <Text fontSize="xs" color={colors.mutedColor} mt={1}>{c.sub}</Text>}
                </Box>
                <Box bg={`${c.color}.50`} color={`${c.color}.600`} p={2} borderRadius="lg">
                  <Icon as={c.icon} boxSize={4} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {error && (
        <Alert status="error" borderRadius="md" mb={6} alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>Couldn't load today's tasks</AlertTitle>
            <AlertDescription fontSize="sm">
              {error}
              {/^column .* does not exist|could not find the/i.test(error)
                ? ' — a database migration may not be applied on this environment.'
                : ''}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {!error && allClear && (
        <Card bg={colors.cardBg} borderWidth="1px" borderColor="green.200" shadow="sm" mb={6}>
          <CardBody>
            <HStack spacing={3}>
              <Icon as={FaCheckCircle} color="green.500" boxSize={6} />
              <Box>
                <Heading size="sm" color="green.700">All clear for today 🎉</Heading>
                <Text fontSize="sm" color={colors.mutedColor}>No phlebs in onboarding, no patients to call, no routes or handoffs outstanding.</Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* ─── 1. Onboard phlebs ───────────────────────────────────────── */}
        <SectionCard
          icon={FaUserPlus} color="blue" colors={colors}
          title="Onboard phlebs" subtitle="Pipeline from invite → route-ready"
          count={counts.onboarding}
        >
          {loading ? <Flex py={6} justify="center"><Spinner /></Flex>
            : onboarding.length === 0 ? <EmptyRow label="No phlebs in onboarding" colors={colors} />
            : onboarding.map((p) => (
              <Row key={p.id} colors={colors} onClick={() => navigate('/admin/phlebotomists')}>
                <Box flex={1} minW={0}>
                  <HStack spacing={2} mb={0.5}>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>{p.full_name || p.company_name || p.email || 'Unnamed phleb'}</Text>
                    <Badge colorScheme={p.stage.color} fontSize="2xs" borderRadius="full">{p.stage.label}</Badge>
                  </HStack>
                  <Text fontSize="xs" color={colors.mutedColor} noOfLines={1}>
                    {p.company_name ? `${p.company_name} · ` : ''}{ageLabel(p.anchor) ? `in stage ${ageLabel(p.anchor)}` : 'not started'}
                  </Text>
                </Box>
                {p.stage.code === 'awaiting_supplies' ? (
                  <Button
                    size="xs" colorScheme="orange" leftIcon={<Icon as={FaBoxOpen} />}
                    isLoading={savingId === `supplies-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); markSuppliesPickedUp(p); }}
                  >
                    Mark supplies
                  </Button>
                ) : (
                  <Icon as={FaArrowRight} color={colors.mutedColor} boxSize={3} flexShrink={0} />
                )}
              </Row>
            ))}
        </SectionCard>

        {/* ─── 2. Call patients ────────────────────────────────────────── */}
        <SectionCard
          icon={FaPhoneAlt} color="red" colors={colors}
          title="Call patients" subtitle="Overdue & uncontacted draws (3h SLA)"
          count={counts.calls}
        >
          {loading ? <Flex py={6} justify="center"><Spinner /></Flex>
            : callQueue.length === 0 ? <EmptyRow label="No patients need a call" colors={colors} />
            : callQueue.map((s) => {
              const info = getNextUpdateInfo(s);
              return (
                <Row key={s.id} colors={colors} onClick={() => navigate(`/admin/submissions/${s.id}`)}>
                  <Box flex={1} minW={0}>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{s.patient_name || 'Unknown patient'}</Text>
                      {s.stat_test && <Badge colorScheme="red" fontSize="2xs">STAT</Badge>}
                      {info.state === 'overdue' && <Badge colorScheme="red" fontSize="2xs">Overdue {fmtDur(info.dueMs)}</Badge>}
                      {info.state === 'none' && <Badge colorScheme="orange" fontSize="2xs">No contact yet</Badge>}
                      {s.followup_required && info.state !== 'overdue' && info.state !== 'none' && <Badge colorScheme="yellow" fontSize="2xs">Follow up</Badge>}
                    </HStack>
                    <Text fontSize="xs" color={colors.mutedColor} noOfLines={1}>
                      {s.draw_code ? `${s.draw_code} · ` : ''}{s.outcome_code ? s.outcome_code.replace(/_/g, ' ') : 'no outcome logged'}
                    </Text>
                  </Box>
                  {s.patient_phone && (
                    <IconButton
                      as="a" href={`tel:${s.patient_phone}`} aria-label="Call patient"
                      icon={<Icon as={FaPhoneAlt} />} size="xs" colorScheme="green" variant="outline"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Row>
              );
            })}
        </SectionCard>

        {/* ─── 3. Assign routes ────────────────────────────────────────── */}
        <SectionCard
          icon={FaRoute} color="purple" colors={colors}
          title="Assign routes" subtitle="Unassigned draws due today & tomorrow"
          count={counts.routes}
          footer={
            <Button mt={3} size="sm" variant="outline" w="100%" rightIcon={<Icon as={FaArrowRight} />} onClick={() => navigate('/admin/active-routes')}>
              Open Active Routes
            </Button>
          }
        >
          {loading ? <Flex py={6} justify="center"><Spinner /></Flex>
            : routeQueue.length === 0 ? <EmptyRow label="Every upcoming draw is assigned" colors={colors} />
            : routeQueue.map((s) => {
              const d = new Date(s.blood_collection_time);
              const when = isToday(d) ? 'Today' : isTomorrow(d) ? 'Tomorrow' : d.toLocaleDateString();
              const overdue = d < startOfDay(new Date());
              return (
                <Row key={s.id} colors={colors} onClick={() => navigate(`/admin/submissions/${s.id}`)}>
                  <Box flex={1} minW={0}>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{s.patient_name || 'Unknown patient'}</Text>
                      {s.stat_test && <Badge colorScheme="red" fontSize="2xs">STAT</Badge>}
                      <Badge colorScheme={overdue ? 'red' : isToday(d) ? 'purple' : 'gray'} fontSize="2xs">
                        {overdue ? 'Past due' : when}
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color={colors.mutedColor} noOfLines={1}>
                      {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}{s.draw_code ? ` · ${s.draw_code}` : ''}
                    </Text>
                  </Box>
                  <Badge variant="subtle" colorScheme="orange" fontSize="2xs" flexShrink={0}>Unassigned</Badge>
                </Row>
              );
            })}
        </SectionCard>

        {/* ─── 4. Specimen handoff ─────────────────────────────────────── */}
        <SectionCard
          icon={FaTruck} color="teal" colors={colors}
          title="Specimen handoff" subtitle="Drawn — coordinate drop-off / pickup"
          count={counts.handoff}
        >
          {loading ? <Flex py={6} justify="center"><Spinner /></Flex>
            : handoffQueue.length === 0 ? <EmptyRow label="No specimens awaiting handoff" colors={colors} />
            : handoffQueue.map((s) => {
              const method = s.handoff_method; // 'dropoff' | 'pickup' | null
              const needsLocation = method === 'dropoff' && !s.drop_off_location_id;
              const canMark = method === 'pickup' ? !!s.pickup_location : !!s.drop_off_location_id;
              return (
                <Row key={s.id} colors={colors}>
                  <Box flex={1} minW={0}>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{s.patient_name || 'Unknown patient'}</Text>
                      <Badge colorScheme={!method ? 'gray' : method === 'pickup' ? 'blue' : 'teal'} fontSize="2xs">
                        {!method ? 'Method?' : method === 'pickup' ? 'Pickup' : 'Drop-off'}
                      </Badge>
                    </HStack>
                    {needsLocation ? (
                      <Select
                        size="xs" mt={1} placeholder="Assign drop-off location…"
                        value={s.drop_off_location_id || ''}
                        isDisabled={savingId === `loc-${s.id}`}
                        onChange={(e) => assignDropOffLocation(s, e.target.value)}
                      >
                        {dropOffLocations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </Select>
                    ) : (
                      <Text fontSize="xs" color={colors.mutedColor} noOfLines={1}>
                        {method === 'pickup'
                          ? (s.pickup_location || 'No pickup address yet')
                          : (dropOffLocations.find((l) => l.id === s.drop_off_location_id)?.name || s.phlebotomist_name || 'Specimen ready')}
                        {s.draw_code ? ` · ${s.draw_code}` : ''}
                      </Text>
                    )}
                  </Box>
                  {!method ? (
                    <IconButton aria-label="Open submission" icon={<Icon as={FaArrowRight} />} size="xs" variant="ghost" onClick={() => navigate(`/admin/submissions/${s.id}`)} />
                  ) : (
                    <Button
                      size="xs" colorScheme="teal" leftIcon={<Icon as={FaRegCircle} />}
                      isDisabled={!canMark}
                      isLoading={savingId === `handoff-${s.id}`}
                      onClick={() => markHandedOff(s)}
                      flexShrink={0}
                    >
                      {method === 'pickup' ? 'Picked up' : 'Dropped off'}
                    </Button>
                  )}
                </Row>
              );
            })}
        </SectionCard>
      </SimpleGrid>
    </Box>
  );
}

export default DailyTasks;
